import React, { useMemo } from 'react';
import { useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import z from 'zod';
import { emailSchema } from '../../utils/schemas';
import Input from '../../components/custom-components/Inputs/Input';
import ZaraButton from '../../components/custom-components/buttons/Button';
import { PATHS } from '../../router/routes';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@apollo/client';
import { SendForgotPassword } from '../../graphql/mutations/SendForgotPassword';
import { useError } from '../../error-provider/ErrorProvider';
import { CloseIcon } from '@chakra-ui/icons';

const schema = z.object({ email: emailSchema });

const ForgotPassword = () => {
    const router = useNavigate();
    const { showError } = useError();
    const toast = useToast();
    const { control, watch, handleSubmit } = useForm({
        defaultValues: {
            email: '',
        },
        mode: 'onSubmit',
        resolver: zodResolver(schema),
    });

    const [onForgotPassword, { loading }] = useMutation(SendForgotPassword, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const email = watch('email');

    const isDisabled = useMemo(() => String(email).trim().length === 0, [email]);

    async function onSubmit(data) {
        const variables = {
            email: data.email.toLowerCase(),
        };
        await onForgotPassword({
            variables: variables,
            onCompleted(data) {
                if (data.sendForgotPassword.__typename === 'ErrorType') {
                    toast({
                        status: 'error',
                        title: data.sendForgotPassword.message,
                        isClosable: true,
                    });
                }
                if (data.sendForgotPassword.__typename === 'SuccessType') {
                    toast({
                        status: 'success',
                        title: data.sendForgotPassword.message,
                        isClosable: true,
                    });
                    localStorage.setItem('email', variables.email);
                    localStorage.setItem('fromWhere', PATHS.FORGOT_PASSWORD);
                    router(PATHS.VERIFICATION);
                }
            },
        });
    }

    return (
        <div className='min-h-screen bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center p-4'>
            <div className='w-full max-w-md space-y-8 bg-white p-6 rounded-xl shadow-2xl relative'>
                <button
                    className='absolute top-2 right-2 text-gray-500 hover:text-gray-700'
                    onClick={() => router(PATHS.SIGN_IN)}
                >
                    <CloseIcon />
                </button>
                <div className='text-center'>
                    <h2 className='mt-6 text-3xl font-bold text-gray-900'>Forgot Password</h2>
                    <p className='mt-2 text-sm text-gray-600'>
                        If you have lost your password, we can help you reset it. To initiate the password reset
                        process, please enter your email in the box below and click on the button.
                    </p>
                </div>
                <form className='mt-8 space-y-6' onSubmit={handleSubmit(onSubmit)}>
                    <Controller
                        control={control}
                        name='email'
                        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                            <Input
                                borderRadius='4px'
                                p='0px 16px'
                                placeholder='Email address'
                                error={error?.message}
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                            />
                        )}
                    />
                    <ZaraButton isDisabled={isDisabled || loading} type='submit' variant='primary' w='full'>
                        Reset Password
                    </ZaraButton>
                </form>
                <div className='text-sm text-center'>
                    <button
                        className='font-medium text-indigo-600 hover:text-indigo-500'
                        onClick={() => router(PATHS.SIGN_IN)}
                    >
                        Back to Login
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
